/**
 * NFC Solutions — Coordinator Mesh Router (Phase 2, Session 27.55+)
 * -----------------------------------------------------------------
 *
 * This module is the SENDER + RECEIVER decision layer that sits on
 * top of `lanMesh.js`. Every mutation in the app (place order, add
 * items, record payment, void, close table, tip) flows through
 * `routeWriteThroughCoordinator(envelope)` — this file — which
 * decides where the write lives:
 *
 *   ┌───────────────────────────────────────────────────────────┐
 *   │  I am the elected Coordinator today                       │
 *   │  → apply reducer locally + queue for cloud sync (default) │
 *   ├───────────────────────────────────────────────────────────┤
 *   │  I am NOT the Coordinator AND the Coordinator peer is on  │
 *   │  the LAN mesh                                             │
 *   │  → send envelope to it over mesh, wait <= 1s for ACK,     │
 *   │    then DO NOT queue for cloud (Coordinator handles that) │
 *   ├───────────────────────────────────────────────────────────┤
 *   │  Either no Coordinator was elected, OR the elected        │
 *   │  Coordinator is unreachable (offline, network partition,  │
 *   │  browser mode) → fall through to the classic behaviour:   │
 *   │  queue for cloud sync via eventSync.js                    │
 *   └───────────────────────────────────────────────────────────┘
 *
 * Dedup safety
 * ------------
 * Every envelope carries a client-generated `change_id` (UUID).
 * `POST /api/events/sync` on the backend already dedupes by
 * `change_id` — so even if the same envelope reaches the cloud
 * twice (once via mesh-to-coordinator-to-cloud, once via a client
 * retry that bypasses the mesh), the second write is a no-op. This
 * is the guarantee the owner asked for: "no double orders even on
 * retry storms".
 *
 * Optimistic UI
 * -------------
 * The caller (`eventEngine.localWrite`) has ALREADY applied the
 * reducer locally by the time it hands us an envelope, so this
 * module can never affect first-paint latency. Any mesh timeout
 * (>1s) simply falls back to cloud — the UI is oblivious.
 */

import { sendMeshEvent, subscribeMesh, getMeshStatus } from "./lanMesh";
import { applyRemoteEvent } from "./eventEngine";
import { putOne } from "./localDb";

// ─── module state ─────────────────────────────────────────────────
// Mutated only by `configureCoordinatorMesh` / election events —
// never by the caller directly. Kept flat & synchronous so
// `routeWriteThroughCoordinator` can make its decision without
// awaiting anything (the decision itself is instant; the mesh call
// awaits ACK internally).
const STATE = {
  isCoordinator: false,          // this device holds the role
  coordinatorDeviceId: null,     // remote device_id that holds it
  businessId: null,
  role: null,                    // "owner" | "staff" | "kitchen" | ...
  telemetry: {
    sentViaMesh: 0,
    sentViaCloud: 0,
    fellBackToCloud: 0,
    inboundFromMesh: 0,
    inboundDeduped: 0,
  },
};

let _meshUnsub = null;

/**
 * Coordinator sub-system runs in two very different modes:
 *   - SENDER: on the leaf devices, we forward writes to the mesh.
 *   - RECEIVER: on the coordinator device, we accept forwarded writes
 *               from the mesh, dedupe, apply, and queue for cloud.
 *
 * `configureCoordinatorMesh` sets which mode we're in. Call it:
 *   • On login (default: NOT coordinator, sender-only)
 *   • When our device wins the election (isCoordinator = true)
 *   • When we hear about who the coordinator is
 *   • On logout / dethrone (reset to defaults)
 */
export function configureCoordinatorMesh({ isCoordinator, coordinatorDeviceId, businessId, role } = {}) {
  if (typeof isCoordinator === "boolean") STATE.isCoordinator = isCoordinator;
  if (coordinatorDeviceId !== undefined) STATE.coordinatorDeviceId = coordinatorDeviceId;
  if (businessId !== undefined) STATE.businessId = businessId;
  if (role !== undefined) STATE.role = role;

  // If we are the coordinator, make sure we're subscribed to inbound
  // mesh events so we can accept writes from leaf devices. If we're
  // no longer the coordinator, unsubscribe so we don't accidentally
  // process events meant for whoever DOES hold the role.
  if (STATE.isCoordinator) {
    _ensureCoordinatorInboundListener();
  } else {
    _teardownCoordinatorInboundListener();
  }
}

export function getCoordinatorMeshState() {
  return {
    isCoordinator: STATE.isCoordinator,
    coordinatorDeviceId: STATE.coordinatorDeviceId,
    businessId: STATE.businessId,
    role: STATE.role,
    telemetry: { ...STATE.telemetry },
  };
}

// Exposed for DevTools — clears counters so an operator inspecting
// mesh health can see "in the last 5 minutes we sent X via mesh, Y
// via cloud".
export function resetCoordinatorMeshTelemetry() {
  STATE.telemetry.sentViaMesh = 0;
  STATE.telemetry.sentViaCloud = 0;
  STATE.telemetry.fellBackToCloud = 0;
  STATE.telemetry.inboundFromMesh = 0;
  STATE.telemetry.inboundDeduped = 0;
}


// ─── SENDER SIDE ──────────────────────────────────────────────────

/**
 * Decides where an envelope should be persisted. Returns one of:
 *   { route: "self",  reason }  → I AM the coordinator; caller should
 *                                  queue for cloud sync via the
 *                                  normal `eventSync.js` pipeline.
 *   { route: "mesh",  reason }  → I sent it over the mesh and got an
 *                                  ACK from the coordinator; the
 *                                  caller MUST NOT also queue for
 *                                  cloud (the coordinator will).
 *   { route: "cloud", reason }  → No mesh path; caller SHOULD queue
 *                                  for cloud via the normal pipeline
 *                                  (this is the pre-Phase-2 default).
 *
 * The function itself will actually send over the mesh when route
 * is "mesh"; the caller doesn't need to know how. Kept as a single
 * awaitable so the mesh timeout (1s) doesn't leak into the UI's
 * write path — which has already been repainted optimistically.
 */
export async function routeWriteThroughCoordinator(envelope) {
  if (!envelope || !envelope.change_id) {
    STATE.telemetry.sentViaCloud += 1;
    return { route: "cloud", reason: "invalid-envelope" };
  }

  // Case 1 — I AM the coordinator. The caller's next step is the
  // usual cloud queue. Nothing to route.
  if (STATE.isCoordinator) {
    STATE.telemetry.sentViaCloud += 1;
    return { route: "self", reason: "i-am-coordinator" };
  }

  // Case 2 — No coordinator elected today, OR mesh not connected.
  // Fall back to the pre-Phase-2 cloud sync path so the write still
  // reaches the server. This is the safety net that keeps the app
  // working when the coordinator is offline / an owner hasn't
  // elected one yet on a fresh morning.
  const meshStatus = getMeshStatus();
  if (!STATE.coordinatorDeviceId) {
    STATE.telemetry.sentViaCloud += 1;
    return { route: "cloud", reason: "no-coordinator-elected" };
  }
  // Session 30.1 — CRITICAL FIX. This gate previously compared against
  // `"connected"`, a status string `getMeshStatus()` NEVER returns
  // (real values: "lan" | "cloud" | "offline" | "idle"). The result
  // was that EVERY leaf write silently fell back to cloud with
  // reason "mesh-offline" — LAN forwarding to the coordinator never
  // happened, which is exactly why "everything stops when the
  // internet dies even though the router is up". We now route via
  // mesh whenever at least one WebRTC data channel is open
  // (peerCount > 0 implies ready peers exist), regardless of the
  // latched status label.
  if (!meshStatus || (meshStatus.peerCount || 0) === 0) {
    STATE.telemetry.sentViaCloud += 1;
    return { route: "cloud", reason: "mesh-offline" };
  }

  // Case 3 — Try to send via mesh. `sendMeshEvent` returns
  // `{delivered, via, latencyMs}` and its own hard 1s ACK timeout —
  // we don't need our own.
  const meshEnvelope = { ...envelope, _forwarded_to_coordinator: true };
  const result = await sendMeshEvent(meshEnvelope, { targetRole: "*" });

  if (result && result.delivered) {
    STATE.telemetry.sentViaMesh += 1;
    return { route: "mesh", reason: "acked", latencyMs: result.latencyMs || null };
  }

  // Mesh delivery failed (no peer / ACK timeout / bad envelope) →
  // fall through to cloud so the write is not lost. Ticking the
  // fell-back counter separately lets us see, in DevTools, whether
  // the LAN mesh is silently degrading below expectations.
  STATE.telemetry.fellBackToCloud += 1;
  STATE.telemetry.sentViaCloud += 1;
  return { route: "cloud", reason: `mesh-miss:${result?.reason || "unknown"}` };
}


// ─── RECEIVER SIDE (COORDINATOR ONLY) ─────────────────────────────

/**
 * When THIS device is the coordinator, `configureCoordinatorMesh`
 * subscribes to the mesh so we can process events forwarded by
 * leaf devices. Handler is idempotent — every envelope has a
 * `change_id` and we insert into `event_queue` with that as the
 * primary key. IndexedDB's uniqueness constraint means a duplicate
 * from a retry is a no-op.
 */
function _ensureCoordinatorInboundListener() {
  if (_meshUnsub) return; // already subscribed
  _meshUnsub = subscribeMesh(async (msg) => {
    if (!msg || typeof msg !== "object") return;
    // We only care about "event" messages that were explicitly
    // forwarded to a coordinator; the mesh also carries other
    // realtime traffic (kitchen ready pings, waiter calls) that
    // has its own handler.
    if (!msg._forwarded_to_coordinator) return;
    if (!msg.change_id) return;

    STATE.telemetry.inboundFromMesh += 1;

    // Dedupe: has this change_id already been persisted?
    // `putOne` on the "event_queue" store with the same key is
    // effectively an upsert — we detect "already there" by reading
    // first (a small extra IDB read, but it avoids re-applying the
    // reducer and re-broadcasting `LOCAL_EVENT_APPLIED`).
    let alreadyKnown = false;
    try {
      const { readOne } = await import("./localDb");
      const existing = await readOne("event_queue", msg.change_id);
      if (existing) alreadyKnown = true;
    } catch (_) { /* worst case we re-apply — reducer is idempotent */ }

    if (alreadyKnown) {
      STATE.telemetry.inboundDeduped += 1;
      return;
    }

    // Persist to event_queue with LOCAL status so the existing
    // `eventSync.js` loop picks it up and pushes to /api/events/sync
    // on our next drain tick. The Coordinator therefore becomes the
    // single writer to the cloud for its whole restaurant.
    const row = {
      ...msg,
      id: msg.change_id,
      _meta: {
        ...(msg._meta || {}),
        status: "local",
        queued_at: msg.ts || new Date().toISOString(),
        _received_via_mesh: true,
      },
    };
    try {
      await putOne("event_queue", row);
    } catch (_) { /* IDB failure is rare — skip silently */ }

    // Apply the reducer so the Coordinator's OWN dashboards refresh
    // in real time (a manager watching the Owner dashboard sees new
    // orders come in immediately). `applyRemoteEvent` is idempotent.
    try { await applyRemoteEvent(msg); } catch (_) { /* noop */ }
  });
}

function _teardownCoordinatorInboundListener() {
  if (_meshUnsub) {
    try { _meshUnsub(); } catch (_) { /* noop */ }
    _meshUnsub = null;
  }
}


// ─── convenience shim ─────────────────────────────────────────────

/**
 * A "am I the sender that should skip cloud sync?" helper for the
 * eventSync loop. Returns TRUE only when this device's envelope was
 * successfully handed off to the coordinator via mesh (route === "mesh"
 * in the last routing decision). eventSync uses this to mark the
 * queued row `SYNCED` locally without hitting `/api/events/sync` —
 * because the coordinator will do that for us.
 *
 * Currently we DO NOT plumb this through — we simply let the cloud
 * dedup on `change_id`. Kept as a stub so a future optimisation can
 * skip a redundant HTTP call without changing call sites.
 */
export function shouldSkipCloudForEnvelope(_envelope) {
  return false;
}

// ─── DevTools exposure ────────────────────────────────────────────
// Operators can inspect the coordinator mesh state and telemetry
// from the browser console. Useful when debugging "why did my order
// go via cloud instead of via mesh?" on a live restaurant.
//
//   window.nfcsCoordinatorMesh()  → { isCoordinator, coordinatorDeviceId,
//                                     telemetry: { sentViaMesh, sentViaCloud, ... } }
//   window.nfcsCoordinatorMeshReset()  → clears counters
if (typeof window !== "undefined") {
  try {
    window.nfcsCoordinatorMesh = () => {
      const snap = getCoordinatorMeshState();
      // eslint-disable-next-line no-console
      console.log("[nfcsCoordinatorMesh]", snap);
      return snap;
    };
    window.nfcsCoordinatorMeshReset = () => {
      resetCoordinatorMeshTelemetry();
      // eslint-disable-next-line no-console
      console.log("[nfcsCoordinatorMeshReset] telemetry cleared");
    };
  } catch (_) { /* noop */ }
}
